import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import API from "../../services/api";
import { Modal } from "bootstrap";
import { toast } from "react-toastify";
import Swal from "sweetalert2";

import UserTable from "./UserTable";
import UserModal from "./UserModal";

const UserList = () => {
  const [submitLoading, setSubmitLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [validated, setValidated] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({
    name: "",
    email: "",
    role: "",
    password: "",
  });
  const [editId, setEditId] = useState(null);
  const [page, setPage] = useState(1);
  const [perPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalData, setTotalData] = useState(0);
  const modalRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 2000);

    return () => clearTimeout(handler);
  }, [search]);

  useEffect(() => {
    fetchUsers(1, debouncedSearch);
  }, [debouncedSearch]);

  const fetchUsers = async (page = 1, search = "") => {
    setTableLoading(true);
    try {
      const res = await API.get("/users", {
        params: {
          page: page,
          per_page: perPage,
          search: search,
        },
      });
      setUsers(res.data.data.data);
      setTotalPages(res.data.meta.last_page);
      setTotalData(res.data.meta.total);
      setTableLoading(false);
    } catch (err) {
      setTableLoading(false);
      toast.error("Sesi habis. Silakan login ulang.");
      navigate("/login");
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formEl = e.currentTarget;

    if (!formEl.checkValidity()) {
      e.stopPropagation();
      setValidated(true);
      return;
    }

    
    try {
      const dataToSend = {
        name: form.name,
        email: form.email,
        // password: form.password,
        role: form.role,
      };

      if (form.password) {
        dataToSend.password = form.password;
      }

      setSubmitLoading(true);
      if (editId) {
        await API.put(`/users/${editId}`, dataToSend);
        toast.info("User berhasil diperbarui.");
      } else {
        await API.post("/users", dataToSend);
        toast.success("User berhasil ditambah.");
      }

      setForm({ name: "", email: "", password: "", role: "" });
      setEditId(null);
      fetchUsers();

      const modalInstance =
        Modal.getInstance(modalRef.current) || new Modal(modalRef.current);
      modalInstance.hide();

      document.body.classList.remove("modal-open");
      document.querySelectorAll(".modal-backdrop").forEach((el) => el.remove());
      setSubmitLoading(false);
    } catch (err) {
      const errorMessage =
        err.response?.data?.message || err.message || "Gagal menyimpan user!";
      toast.error(errorMessage);
      setSubmitLoading(false);
    }

    setValidated(true);
  };

  const handleEdit = (user) => {
    setForm({
      name: user.name,
      email: user.email,
      role: user.role,
      password: "",
    });
    setEditId(user.id);
    const modal = new Modal(modalRef.current);
    modal.show();
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Yakin?",
      text: "User akan dihapus secara permanen.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Ya, hapus",
      cancelButtonText: "Batal",
    });
    if (result.isConfirmed) {
      try {
        await API.delete(`/users/${id}`);
        toast.success("User berhasil dihapus!");
        fetchUsers();
      } catch (err) {
        toast.error("Gagal menghapus user.");
      }
    }
  };

  return (
    <div className="container mt-4">
      <div className="align-items-center mb-3">
        <h3>Daftar Users</h3>
      </div>
      <div className="d-flex flex-row-reverse mb-2">
        <div className="col-md-4">
          <div
            className="d-flex border rounded overflow-hidden"
            style={{ height: "38px" }}
          >
            <input
              type="text"
              className="form-control border-0 shadow-none"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ flex: 1 }}
            />
            <span className="d-flex align-items-center px-3 border-start bg-white">
              <i className="fa-solid fa-search text-muted"></i>
            </span>
          </div>
        </div>
      </div>
      <div className="d-flex flex-wrap justify-content-end mb-2">
        <button
          className="btn btn-primary"
          onClick={() => {
            setEditId(null);
            setForm({ name: "", email: "", role: "", password: "" });
            const modal = new Modal(modalRef.current);
            modal.show();
          }}
        >
          Tambah
        </button>
      </div>

      <UserTable
        users={users}
        tableLoading={tableLoading}
        onEdit={handleEdit}
        onDelete={handleDelete}
        page={page}
        perPageSize={perPage}
      />
      <div className="d-flex justify-content-between align-items-center mt-3">
        <span>
          Page {page} of {totalPages} dari {totalData.toLocaleString("id-ID")}
        </span>
        <div>
          <button
            className="btn btn-secondary btn-sm me-2"
            disabled={page === 1}
            onClick={() => {
              const newPage = page - 1;
              setPage(newPage);
              fetchUsers(newPage, search);
            }}
          >
            Previous
          </button>
          <button
            className="btn btn-secondary btn-sm"
            disabled={page === totalPages}
            onClick={() => {
              const newPage = page + 1;
              setPage(newPage);
              fetchUsers(newPage, search);
            }}
          >
            Next
          </button>
        </div>
      </div>

      <UserModal
        modalRef={modalRef}
        form={form}
        handleChange={handleChange}
        handleSubmit={handleSubmit}
        editId={editId}
        setEditId={setEditId}
        setForm={setForm}
        validated={validated}
        submitLoading={submitLoading}
      />
    </div>
  );
};

export default UserList;
